import type { Dispatch } from 'redux';
import { setFeatureFlags, setTenantId } from '@innovyze/lib_get_service';
import { runIdentity } from './phases/runIdentity';
import { runFlags } from './phases/runFlags';
import { setFeatures } from '../../actions/app.actions';
import {
	setDefaultSite,
	setToken,
	setUser,
	setIsAuthenticated,
	setIsInitializing,
} from '../../actions/authenticationContext.actions';
import { setUserContext } from '../../actions/userContext.actions';
import { processUserContextToStore } from '../../utils/UserContext';
import { RegisteredApplications } from '../../types';
import type {
	getUserInfoV2,
	getAutodeskContext,
} from '../../services/forge.service';
import type { getCurrentCompanySettings } from '../../utils/companySettings';
import type { UserProject } from '../../types/project.types';
import type { InitIdentity, InitializationSteps } from './useInitialization';
import {
	composeDataTasks,
	createUserSettingsTask,
	createSubscriptionsTask,
	createCompanySettingsTask,
} from './dataTasks';

interface StepServices {
	getUserInfoV2: typeof getUserInfoV2;
	getAutodeskContext: typeof getAutodeskContext;
	getCurrentCompanySettings: typeof getCurrentCompanySettings;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export interface BuildStepsArgs {
	dispatch: Dispatch;
	/**
	 * Dynamic dependencies are read through getters so the steps always see the
	 * latest value at await-time. This lets the initializer mount high/early
	 * (before config or identity have resolved) without freezing stale values.
	 */
	getConfig: () => any;
	getAppName: () => RegisteredApplications;
	getCurrentApp: () => string;
	/** Active project for the project-injection (tenant-switch) flow. */
	getActiveProject: () => UserProject | undefined;
	isLandingPage: boolean;
	i18n: { changeLanguage: (lng: string) => void };
	getAccessToken: () => string | null;
	/**
	 * True when the app loaded on a /projects/<sourceId> (tenant-switch) URL. In
	 * that case the identity step waits for the target project to resolve before
	 * fetching, so we issue a single getAutodeskContext(hub, project) call rather
	 * than a null/null bootstrap followed by a corrective re-fetch.
	 */
	isProjectInjectionLoad?: boolean;
	services: StepServices;
}
/* eslint-enable @typescript-eslint/no-explicit-any */

/** Max time the identity step waits for a switched-to project to resolve. */
const PROJECT_RESOLVE_TIMEOUT_MS = 10000;

/**
 * Poll `read` until it returns a truthy value or the timeout elapses; resolves
 * the value, or `undefined` on timeout (so callers can fall back rather than
 * hang).
 */
const pollFor = <T>(
	read: () => T | null | undefined,
	timeoutMs: number,
	intervalMs = 100,
): Promise<T | undefined> =>
	new Promise(resolve => {
		const start = Date.now();
		const tick = () => {
			const value = read();
			if (value) {
				resolve(value);
				return;
			}
			if (Date.now() - start >= timeoutMs) {
				resolve(undefined);
				return;
			}
			setTimeout(tick, intervalMs);
		};
		tick();
	});

export const buildInitializationSteps = (
	args: BuildStepsArgs,
): InitializationSteps => {
	const {
		dispatch,
		getConfig,
		getAppName,
		getCurrentApp,
		getActiveProject,
		isLandingPage,
		isProjectInjectionLoad = false,
		i18n,
		getAccessToken,
		services,
	} = args;

	const poll = <T>(read: () => T | null | undefined, ms = 100): Promise<T> =>
		new Promise(resolve => {
			const tick = () => {
				const value = read();
				if (value) resolve(value);
				else setTimeout(tick, ms);
			};
			tick();
		});

	return {
		awaitConfig: () =>
			poll(() => (getConfig() ? true : null)).then(() => undefined),

		awaitToken: () => poll(() => getAccessToken()),

		runIdentityStep: async (accessToken: string) => {
			const { userProfile, autodeskUserContext } = await runIdentity({
				fetchUserProfile: () =>
					services.getUserInfoV2(getConfig()?.forge, accessToken),
				// Single context method: hub/project ids when a project is
				// resolved (switches/targets that tenant), null/null otherwise
				// (returns the current active tenant per the X-Tenant-Id header).
				fetchAutodeskContext: async () => {
					if (isLandingPage) return null;
					let activeProject = getActiveProject();
					// Tenant-switch load: the target project resolves into Redux
					// shortly after init starts. Wait for it so we issue a single
					// getAutodeskContext(hub, project) call instead of a null/null
					// bootstrap + corrective re-fetch. Bounded so a project that
					// never resolves (e.g. no access) falls back to null/null.
					if (isProjectInjectionLoad && !activeProject) {
						activeProject = await pollFor(
							() => getActiveProject(),
							PROJECT_RESOLVE_TIMEOUT_MS,
						);
					}
					return services.getAutodeskContext(
						getAppName(),
						activeProject?.hubUrn ?? null,
						activeProject?.id ?? null,
					);
				},
			});
			const { defaultSite, userContext } = processUserContextToStore(
				{ autodeskUserContext, userProfile, accessToken },
				isLandingPage,
			);
			dispatch(setToken(accessToken));
			dispatch(setUser(userProfile));
			dispatch(setUserContext(userContext));
			dispatch(
				setDefaultSite(!isLandingPage ? defaultSite : 'LandingPage'),
			);
			if (!isLandingPage) setTenantId(defaultSite);
			dispatch(setIsAuthenticated(true));
			dispatch(setIsInitializing(false));
			const defaultSiteProjectId = userContext.defaultSiteProjectId ?? '';
			return {
				id: userContext.id ?? '',
				email: userContext.email ?? '',
				defaultSite: userContext.defaultSite ?? '',
				defaultSiteProjectId,
				defaultTeamId: userContext.defaultTeamId ?? '',
				entitlements: userContext.entitlements ?? [],
			};
		},

		runFlagsStep: async (identity: InitIdentity) => {
			const {
				id,
				email,
				defaultSite: siteId,
				defaultTeamId: teamUrn,
			} = identity;
			const { flags } = await runFlags({
				clientSideID: getConfig()?.launchDarkly?.clientSideId,
				liveUpdates: getConfig()?.launchDarkly?.liveUpdates,
				context: { key: id, email, custom: { siteId, teamUrn } },
				onChange: changed => {
					dispatch(setFeatures(changed));
					setFeatureFlags(changed);
				},
			});
			dispatch(setFeatures(flags));
			setFeatureFlags(flags);
		},

		runDataStep: composeDataTasks([
			createUserSettingsTask({ dispatch }),
			createSubscriptionsTask({ dispatch }),
			createCompanySettingsTask({
				dispatch,
				getCurrentApp,
				i18n,
				getCurrentCompanySettings: services.getCurrentCompanySettings,
			}),
		]),
	};
};
